import React from 'react';
import { motion } from 'framer-motion';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

const InvSlide08Landscape: React.FC = () => {
  const competitors = [
    {
      name: 'Mathletics',
      issue: 'Developed 20-25 years ago. Flash converted to iOS. Hasn\'t been upgraded in 20 years.',
      hasTeacherTools: false,
      hasMastery: false,
      hasParentView: false,
    },
    {
      name: 'Reading Eggs',
      issue: 'Teachers give random lessons. No management. Bullshit dashboards.',
      hasTeacherTools: false,
      hasMastery: false,
      hasParentView: false,
    },
    {
      name: 'MathSpace',
      issue: 'Has "mastery" but it\'s just XP along a graph. Not true curriculum-level tracking.',
      hasTeacherTools: true,
      hasMastery: false,
      hasParentView: false,
    },
    {
      name: 'Google Classroom',
      issue: 'Docs get cloned, can\'t resubmit. As a parent, I don\'t understand deadlines.',
      hasTeacherTools: true,
      hasMastery: false,
      hasParentView: false,
    },
  ];

  const lumen8Features = [
    'Detailed mastery tracking (680 modules)',
    'Multiple subjects in one platform',
    'Dual reward system (school + home)',
    'Real-time parent visibility',
    'AI-generated adaptive content',
    'Profile follows K-12, school to school',
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full px-16">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-5xl font-bold text-white mb-3 text-center"
      >
        The Current Landscape
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-xl text-[#94a3b8] mb-8 text-center"
      >
        Existing tools are outdated, fragmented, and frustrating
      </motion.p>

      <div className="flex gap-8 w-full max-w-6xl">
        {/* Left: Competitors */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <h3 className="text-xl font-semibold text-[#ef4444] mb-4">The Competition</h3>

          <div className="space-y-3">
            {competitors.map((comp) => (
              <div
                key={comp.name}
                className="p-4 rounded-xl"
                style={{
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-semibold text-white">{comp.name}</span>
                  <div className="flex gap-2">
                    <span className="flex items-center gap-1 text-xs">
                      {comp.hasTeacherTools ? (
                        <CheckCircleIcon className="w-4 h-4 text-[#10b981]" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-[#ef4444]" />
                      )}
                      <span className="text-[#64748b]">Teacher</span>
                    </span>
                    <span className="flex items-center gap-1 text-xs">
                      {comp.hasMastery ? (
                        <CheckCircleIcon className="w-4 h-4 text-[#10b981]" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-[#ef4444]" />
                      )}
                      <span className="text-[#64748b]">Mastery</span>
                    </span>
                    <span className="flex items-center gap-1 text-xs">
                      {comp.hasParentView ? (
                        <CheckCircleIcon className="w-4 h-4 text-[#10b981]" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-[#ef4444]" />
                      )}
                      <span className="text-[#64748b]">Parent</span>
                    </span>
                  </div>
                </div>
                <p className="text-sm text-[#94a3b8]">{comp.issue}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right: Lumen8 */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="w-80"
        >
          <h3 className="text-xl font-semibold text-[#10b981] mb-4">Lumen8 Advantage</h3>

          <div
            className="p-5 rounded-xl"
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '2px solid rgba(16, 185, 129, 0.4)',
            }}
          >
            <div className="space-y-3">
              {lumen8Features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" />
                  <span className="text-white">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="mt-4 p-4 rounded-xl text-center"
            style={{
              background: 'rgba(14, 165, 233, 0.15)',
              border: '1px solid rgba(14, 165, 233, 0.3)',
            }}
          >
            <p className="text-white font-medium">
              "Nobody does a mastery system as detailed as ours"
            </p>
          </div>
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="mt-6 text-lg text-[#ef4444] italic"
      >
        "Those guys are in Aruba on a beach and the thing hasn't been upgraded in 20 years."
      </motion.p>
    </div>
  );
};

export default InvSlide08Landscape;
